import api from './api';

/** Eine Nachricht aus dem echten Postfach (ueber mail-inbox). */
export interface PostfachNachricht {
  uid: number;
  from?: string;
  subject?: string;
  date?: string;
  text?: string;
  html?: string;
}

export interface OutreachActivity {
  id: string;
  lemlistActivityId: string;
  type: string;
  kanal: string;
  campaignId?: string;
  campaignName?: string;
  leadEmail?: string;
  leadName?: string;
  companyName?: string;
  text?: string;
  occurredAt: string;
  handled: boolean;
  handledAt?: string;
  note?: string;
  leadId?: string;
  /** Firmenakte, sobald der Nachtrag die Reaktion zugeordnet hat. */
  companyId?: string | null;
  createdAt: string;
  updatedAt: string;
}

export interface InboxSummary {
  offeneAntworten: number;
  alleAntworten: number;
  gesamt: number;
  nichtInteressiert: number;
  unzustellbar: number;
}

export interface SyncReport {
  kampagnen: number;
  gesehen: number;
  relevant: number;
  neu: number;
  aktualisiert: number;
  leadsMarkiert: number;
  /** Adressen, die der Lauf neu auf die Sperrliste gesetzt hat. */
  gesperrt: number;
  fehler: string[];
}

export interface InboxFilter {
  handled?: boolean;
  kanal?: string;
  nurAntworten?: boolean;
  limit?: number;
}

/** Eine Nachricht aus dem Lemlist-Verlauf. Die Felder variieren je Kanal. */
export interface ThreadNachricht {
  _id?: string;
  type?: string;
  direction?: string;
  from?: string;
  to?: string;
  subject?: string;
  text?: string;
  body?: string;
  createdAt?: string;
  sentAt?: string;
}

export interface ThreadAntwort {
  eintrag: OutreachActivity;
  nachrichten: ThreadNachricht[];
  hinweis: string | null;
}

export type LeadAktion = 'interested' | 'not-interested' | 'pause' | 'resume';

const outreachInboxService = {
  async getSummary(): Promise<InboxSummary> {
    const response = await api.get('/outreach-inbox/summary');
    return response.data;
  },

  async getActivities(filter?: InboxFilter): Promise<OutreachActivity[]> {
    const response = await api.get('/outreach-inbox', { params: filter || {} });
    return response.data;
  },

  async sync(optionen?: { maxSeitenJeKampagne?: number; nurKampagne?: string }): Promise<SyncReport> {
    const response = await api.post('/outreach-inbox/sync', optionen || {});
    return response.data;
  },

  async setHandled(id: string, handled: boolean, note?: string): Promise<OutreachActivity> {
    const response = await api.patch(`/outreach-inbox/${id}`, { handled, note });
    return response.data;
  },

  async getThread(id: string): Promise<ThreadAntwort> {
    const response = await api.get(`/outreach-inbox/${id}/thread`);
    return response.data;
  },

  async reply(id: string, nachricht: string): Promise<OutreachActivity> {
    const response = await api.post(`/outreach-inbox/${id}/reply`, { nachricht });
    return response.data;
  },

  async setLeadState(id: string, aktion: LeadAktion): Promise<OutreachActivity> {
    const response = await api.post(`/outreach-inbox/${id}/lead-state`, { aktion });
    return response.data;
  },

  /**
   * Volltext einer E-Mail-Antwort aus dem echten Postfach nachschlagen.
   *
   * Lemlist liefert ueber seine API nur Betreff und erste Zeile. Der Server
   * reicht das eigene Token an mail-inbox durch -- man sieht also nur sein
   * eigenes Postfach.
   */
  async postfach(id: string): Promise<{ adresse: string; nachrichten: PostfachNachricht[] }> {
    const response = await api.get(`/outreach-inbox/${id}/postfach`);
    return response.data;
  },
};

export default outreachInboxService;
